import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { Icon } from 'js/components';

/**
 * Match quality tiers for display (when not the top score).
 * score >= 2.5 -> good, >= 2 -> good, >= 1 -> medium, >= 0.5 -> low, else minimal
 */
function getMatchQuality(score) {
  if (score == null) return 'minimal';
  if (score >= 2) return 'good';
  if (score >= 1) return 'medium';
  if (score >= 0.5) return 'low';
  return 'minimal';
}

/** Best match(es) in the list are always green; others use tiered colors. */
function getDisplayQuality(itemScore, maxScore) {
  if (maxScore > 0 && itemScore != null && itemScore === maxScore)
    return 'excellent';
  return getMatchQuality(itemScore);
}

const MATCH_DOT_STYLES = {
  excellent: { backgroundColor: 'var(--success, #28a745)' }, // green – meets all criteria
  good: { backgroundColor: 'var(--info, #17a2b8)' }, // teal – strong match (e.g. name "Story Points")
  medium: { backgroundColor: 'var(--warning, #ffc107)' }, // yellow – reasonable (e.g. name includes "story point")
  low: { backgroundColor: 'var(--secondary, #6c757d)' }, // gray – possible (number only)
  minimal: { backgroundColor: 'var(--light, #e9ecef)' }, // light gray – weak match
};

const matchDotBaseStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 8,
  flexShrink: 0,
};

/**
 * Combobox for selecting a Jira issue field. Uses downshift for accessibility
 * and type-to-filter. Items are { id, name, score? }; score is used to show
 * match quality (colored dot) and sort order. Displays "name (id)" and stores id.
 */
export default function JiraFieldCombobox({
  placeholder = 'Select or type to filter...',
  items = [],
  selectedId,
  selectedName,
  onSelect,
  disabled = false,
  id: inputId,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const userTypedThisSessionRef = useRef(false);
  const cursorRef = useRef(null);
  const [cursor, setCursor] = useState(null);

  const fieldToLabel = (field) =>
    field ? `${field.name || field.id} (${field.id})` : '';

  const selectedField = useMemo(
    () => items.find((f) => f.id === selectedId) || null,
    [items, selectedId]
  );

  const selectedDisplayLabel = useMemo(() => {
    if (selectedField) return fieldToLabel(selectedField);
    if (selectedId && selectedName) return `${selectedName} (${selectedId})`;
    if (selectedId) return selectedId;
    return '';
  }, [selectedField, selectedId, selectedName]);

  const itemsWithTokens = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        tokens: [
          ...item.name.split(/\W+/).map((t) => t.toLowerCase()),
          item.id.toLowerCase(),
        ],
      })),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (!inputValue || !inputValue.trim()) return itemsWithTokens;
    const searchTokens = inputValue.split(/\W+/).map((t) => t.toLowerCase());
    return itemsWithTokens.filter((f) =>
      searchTokens.every((token) => f.tokens.some((t) => t.includes(token)))
    );
  }, [itemsWithTokens, inputValue]);

  const maxScoreInFiltered = useMemo(
    () =>
      Math.max(0, ...filteredItems.map((i) => (i.score != null ? i.score : 0))),
    [filteredItems]
  );

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items: filteredItems,
    selectedItem: selectedField,
    inputValue: isFocused ? inputValue : selectedDisplayLabel || inputValue,
    itemToString: fieldToLabel,
    onInputValueChange: ({ inputValue: next }) => {
      userTypedThisSessionRef.current = true;
      setInputValue(next ?? '');
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) onSelect(selectedItem.id);
    },
    onStateChange: ({ type, selectedItem }) => {
      if (type === useCombobox.stateChangeTypes.ItemClick && selectedItem) {
        onSelect(selectedItem.id);
        setInputValue('');
      }
    },
  });

  const downshiftInputProps = getInputProps({
    id: inputId,
    disabled,
    placeholder,
    ref: cursorRef,
  });

  const displayValue =
    isFocused && !inputValue.trim()
      ? selectedDisplayLabel
      : downshiftInputProps.value;

  useLayoutEffect(() => {
    const input = cursorRef.current;
    if (input && cursor != null) {
      input.setSelectionRange(cursor, cursor);
    }
  }, [cursor, displayValue]);

  const inputProps = {
    ...downshiftInputProps,
    value: displayValue,
    onChange: (e) => {
      setCursor(e.target.selectionStart);
      downshiftInputProps.onChange?.(e);
    },
    onFocus: (e) => {
      userTypedThisSessionRef.current = false;
      setIsFocused(true);
      downshiftInputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      // Only clear selection if user actually cleared the field (typed then deleted), not when they just focused and blurred
      if (!inputValue.trim() && selectedId && userTypedThisSessionRef.current) {
        onSelect('');
      }
      setInputValue('');
      downshiftInputProps.onBlur?.(e);
    },
  };
  const toggleProps = getToggleButtonProps({ disabled });

  return (
    <div className="combobox">
      <div className="input-group limited-width-lg">
        <input
          placeholder={placeholder}
          className="form-control"
          {...inputProps}
        />
        <div className="input-group-append">
          <span aria-label="toggle menu" type="button" {...toggleProps}>
            {isOpen ? (
              <Icon icon={['far', 'chevron-up']} fixedWidth />
            ) : (
              <Icon icon={['far', 'chevron-down']} fixedWidth />
            )}
          </span>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className="combobox-items"
        style={!isOpen ? { display: 'none' } : {}}
      >
        {isOpen &&
          filteredItems.map((item, index) => {
            const quality = getDisplayQuality(item.score, maxScoreInFiltered);
            return (
              <li
                className={classNames({
                  highlighted: highlightedIndex === index,
                  'combobox-item': true,
                })}
                key={`${item.id}-${index}`}
                {...getItemProps({ item, index })}
              >
                <span
                  style={{ ...matchDotBaseStyle, ...MATCH_DOT_STYLES[quality] }}
                  title={
                    quality === 'excellent'
                      ? 'Excellent match (meets all criteria)'
                      : quality === 'good'
                      ? 'Good match'
                      : quality === 'medium'
                      ? 'Reasonable match'
                      : quality === 'low'
                      ? 'Possible match'
                      : 'Weak match'
                  }
                  aria-hidden
                />
                <span>{fieldToLabel(item)}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
